import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import BetaFeedbackWidget from '../../components/feature/BetaFeedbackWidget';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface QuizData {
  question: string;
  options: string[];
  correctAnswer: number;
}

interface AnswerData {
  explanation: string;
  stepByStep: string[];
  formula: string | null;
  practice: string;
  quiz: QuizData;
  nextTopics: string[];
}

interface SavedQuestion {
  id: string;
  question: string;
  subject: string;
  answer: AnswerData;
  created_at: string;
}

export default function AskTeacher() {
  const [question, setQuestion] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<AnswerData | null>(null);
  const [error, setError] = useState('');
  const [selectedQuizAnswer, setSelectedQuizAnswer] = useState<number | null>(null);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [savedQuestions, setSavedQuestions] = useState<SavedQuestion[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [user, setUser] = useState<any>(null);

  const subjects = [
    'Mathematik',
    'Deutsch',
    'Englisch',
    'Physik',
    'Chemie',
    'Biologie',
    'Geschichte',
    'Geographie',
    'Informatik',
    'Allgemein'
  ];

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    if (user) {
      loadSavedQuestions(user.id);
    }
  };

  const loadSavedQuestions = async (userId: string) => {
    const { data, error } = await supabase
      .from('saved_questions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setSavedQuestions(data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!question.trim()) {
      setError('Bitte stelle eine Frage');
      return;
    }

    setLoading(true);
    setError('');
    setAnswer(null);
    setSelectedQuizAnswer(null);
    setShowQuizResult(false);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/ask-teacher`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: question.trim(),
            subject: subject || null
          })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Fehler beim Abrufen der Antwort');
      }

      setAnswer(data.answer);

      // Speichere die Frage, wenn der Benutzer eingeloggt ist
      if (user) {
        await supabase.from('saved_questions').insert({
          user_id: user.id,
          question: question.trim(),
          subject: subject || 'Allgemein',
          answer: data.answer
        });
        loadSavedQuestions(user.id);
      }

    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizAnswer = (index: number) => {
    setSelectedQuizAnswer(index);
    setShowQuizResult(true);
  };

  const loadSavedQuestion = (saved: SavedQuestion) => {
    setQuestion(saved.question);
    setSubject(saved.subject);
    setAnswer(saved.answer);
    setShowHistory(false);
    setSelectedQuizAnswer(null);
    setShowQuizResult(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <i className="ri-book-open-line text-xl text-white"></i>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-orange-500 bg-clip-text text-transparent">
                Learnsnap
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              {user ? (
                <>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap flex items-center space-x-2"
                  >
                    <i className="ri-history-line"></i>
                    <span>Meine Fragen</span>
                  </button>
                  <Link
                    to="/dashboard"
                    className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all whitespace-nowrap"
                  >
                    Dashboard
                  </Link>
                </>
              ) : (
                <Link
                  to="/auth/login"
                  className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all whitespace-nowrap"
                >
                  Anmelden
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Hauptbereich */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-orange-500 rounded-2xl mb-4">
                <i className="ri-question-answer-line text-3xl text-white"></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Frag den Lehrer
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Stelle beliebige Fragen zu deinen Schulfächern und erhalte strukturierte, 
                verständliche Antworten mit Übungen und Quiz
              </p>
            </div>

            {/* Frageformular */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fach (optional)
                  </label>
                  <select
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">Fach auswählen...</option>
                    {subjects.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deine Frage
                  </label>
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="z.B. Wie berechne ich die Fläche eines Kreises?"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
                  />
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                    <i className="ri-error-warning-line text-red-500 text-xl"></i>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium whitespace-nowrap flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Antwort wird generiert...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      <span>Frage stellen</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Antwort */}
            {answer && (
              <div className="space-y-6">
                {/* Erklärung */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-lightbulb-line text-xl text-teal-600"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Kurze Erklärung</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{answer.explanation}</p>
                </div>

                {/* Schritt-für-Schritt */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <i className="ri-list-ordered text-xl text-orange-600"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Schritt-für-Schritt</h2>
                  </div>
                  <div className="space-y-4">
                    {answer.stepByStep.map((step, index) => (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">{index + 1}</span>
                        </div>
                        <p className="text-gray-700 pt-1">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Formel/Merksatz */}
                {answer.formula && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl shadow-lg p-8 border-2 border-purple-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                        <i className="ri-bookmark-line text-xl text-white"></i>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">Merksatz / Formel</h2>
                    </div>
                    <p className="text-lg font-medium text-purple-900">{answer.formula}</p>
                  </div>
                )}

                {/* Übung */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-pencil-line text-xl text-blue-600"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Übung für dich</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{answer.practice}</p>
                </div>

                {/* Mini-Quiz */}
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <i className="ri-question-line text-xl text-green-600"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Mini-Quiz</h2>
                  </div>
                  <p className="text-gray-900 font-medium mb-4">{answer.quiz.question}</p>
                  <div className="space-y-3">
                    {answer.quiz.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuizAnswer(index)}
                        disabled={showQuizResult}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all whitespace-nowrap ${
                          showQuizResult
                            ? index === answer.quiz.correctAnswer
                              ? 'border-green-500 bg-green-50'
                              : index === selectedQuizAnswer
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200 bg-gray-50'
                            : 'border-gray-200 hover:border-teal-500 hover:bg-teal-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-gray-900">{option}</span>
                          {showQuizResult && index === answer.quiz.correctAnswer && (
                            <i className="ri-check-line text-green-600 text-xl"></i>
                          )}
                          {showQuizResult && index === selectedQuizAnswer && index !== answer.quiz.correctAnswer && (
                            <i className="ri-close-line text-red-600 text-xl"></i>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {showQuizResult && (
                    <div className={`mt-4 p-4 rounded-lg ${
                      selectedQuizAnswer === answer.quiz.correctAnswer
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-orange-50 border border-orange-200'
                    }`}>
                      <p className="text-sm font-medium">
                        {selectedQuizAnswer === answer.quiz.correctAnswer
                          ? '🎉 Richtig! Super gemacht!'
                          : '💡 Nicht ganz richtig. Schau dir die Erklärung nochmal an!'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Nächste Themen */}
                <div className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-2xl shadow-lg p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-orange-500 rounded-lg flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xl text-white"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Was du als Nächstes lernen könntest</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {answer.nextTopics.map((topic, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-teal-100 rounded flex items-center justify-center flex-shrink-0">
                            <i className="ri-arrow-right-s-line text-teal-600"></i>
                          </div>
                          <p className="text-sm text-gray-700">{topic}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Beispiel */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 sticky top-24">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <i className="ri-lightbulb-flash-line text-teal-600"></i>
                <span>Beispiel</span>
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Fach:</p>
                  <p className="text-sm text-gray-600">Mathematik</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Frage:</p>
                  <p className="text-sm text-gray-600 italic">
                    "Wie berechne ich die Fläche eines Kreises?"
                  </p>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-3">Du erhältst:</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                      <span className="text-xs text-gray-600">Verständliche Erklärung</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                      <span className="text-xs text-gray-600">Schritt-für-Schritt Anleitung</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                      <span className="text-xs text-gray-600">Formel zum Merken</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                      <span className="text-xs text-gray-600">Übungsaufgabe</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                      <span className="text-xs text-gray-600">Mini-Quiz</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                      <span className="text-xs text-gray-600">Weiterführende Themen</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Gespeicherte Fragen */}
            {showHistory && user && savedQuestions.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <i className="ri-history-line text-teal-600"></i>
                  <span>Meine Fragen</span>
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {savedQuestions.map((saved) => (
                    <button
                      key={saved.id}
                      onClick={() => loadSavedQuestion(saved)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-teal-500 hover:bg-teal-50 transition-all"
                    >
                      <p className="text-xs font-medium text-teal-600 mb-1">{saved.subject}</p>
                      <p className="text-sm text-gray-900 line-clamp-2">{saved.question}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(saved.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Beta Feedback Widget */}
      <BetaFeedbackWidget pageTitle="KI-Lehrer fragen" />
    </div>
  );
}